import { ReactNode, useRef, useState } from 'react';

import ArrowNarrowDown from '@untitled-ui/icons-react/build/esm/ArrowNarrowDown';
import ArrowNarrowUp from '@untitled-ui/icons-react/build/esm/ArrowNarrowUp';
import DotsHorizontal from '@untitled-ui/icons-react/build/esm/DotsHorizontal';
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines';
import { Dropdown } from 'antd';

import './ColumnMenu.scss';

export type SortDirection = 'ascending' | 'descending';

interface ColumnMenuProps {
  title: string;
  activeSort?: SortDirection;
  onSortAscending?: () => void;
  onSortDescending?: () => void;
  /** Filter controls for this column. The Filter option only appears when one is given. */
  filterPanel?: ReactNode;
  isFiltered?: boolean;
}

export const ColumnMenu = ({
  title,
  activeSort,
  onSortAscending,
  onSortDescending,
  filterPanel,
  isFiltered,
}: ColumnMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'menu' | 'filter'>('menu');
  // Choosing Filter closes the menu the same way choosing a sort does, so that one
  // close is suppressed to leave the panel showing in its place.
  const keepOpenRef = useRef(false);

  const handleOpenChange = (open: boolean) => {
    if (!open && keepOpenRef.current) {
      keepOpenRef.current = false;
      return;
    }

    setIsOpen(open);
    if (!open) setView('menu');
  };

  const items = [
    {
      key: 'ascending',
      icon: <ArrowNarrowUp className="w-4 h-4 text-gray-500" />,
      label: 'Sort ascending',
      onClick: onSortAscending,
    },
    {
      key: 'descending',
      icon: <ArrowNarrowDown className="w-4 h-4 text-gray-500" />,
      label: 'Sort descending',
      onClick: onSortDescending,
    },
    ...(filterPanel
      ? [
          {
            type: 'divider' as const,
          },
          {
            key: 'filter',
            icon: <FilterLines className="w-4 h-4 text-gray-500" />,
            label: 'Filter',
            onClick: () => {
              keepOpenRef.current = true;
              setView('filter');
            },
          },
        ]
      : []),
  ];

  return (
    <Dropdown
      menu={{ items, selectable: true, selectedKeys: activeSort ? [activeSort] : [] }}
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="column-menu"
      dropdownRender={(menuNode) => (view === 'filter' ? <>{filterPanel}</> : menuNode)}
    >
      <button
        type="button"
        aria-label={`${title} column options`}
        className={`column-menu-trigger group flex w-full cursor-pointer items-center justify-between gap-2 text-left transition-colors ${
          isOpen ? 'bg-gray-200' : 'hover:bg-gray-100'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <span className="flex items-center gap-1">
          {title}
          {activeSort === 'ascending' && <ArrowNarrowUp className="w-3.5 h-3.5 text-gray-500" />}
          {activeSort === 'descending' && <ArrowNarrowDown className="w-3.5 h-3.5 text-gray-500" />}
          {isFiltered && <FilterLines className="w-3.5 h-3.5 text-primary-600" />}
        </span>
        <DotsHorizontal className="w-4 h-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600" />
      </button>
    </Dropdown>
  );
};
